// Notifications register: full history with All/Unread filter, mark-all-read,
// per-row delete, offset pagination. Record Book ruled-list idiom.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TrashIcon } from '@heroicons/react/24/outline';
import apiService from '../../services/api';
import { AppNotification } from '../../types';
import { timeAgo } from '../../utils/timeAgo';

const PAGE_SIZE = 50;

export default function NotificationsPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState<AppNotification[]>([]);
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const loadGen = useRef(0);

  const load = useCallback(async (unreadOnly: boolean, offset: number) => {
    const gen = ++loadGen.current;
    setLoading(true);
    setError(null);
    try {
      const rows = await apiService.getNotifications({
        unread_only: unreadOnly, limit: PAGE_SIZE, offset,
      });
      if (gen !== loadGen.current) return; // stale response; a newer load superseded it
      setItems((prev) => (offset === 0 ? rows : [...prev, ...rows]));
      setHasMore(rows.length === PAGE_SIZE);
    } catch (e: any) {
      if (gen !== loadGen.current) return;
      setError(e.message || 'Failed to load notifications');
    } finally {
      if (gen === loadGen.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(filter === 'unread', 0);
  }, [filter, load]);

  const handleRowClick = async (n: AppNotification) => {
    if (!n.is_read) {
      setItems((rows) => rows.map((r) => (r.id === n.id ? { ...r, is_read: true } : r)));
      try {
        await apiService.markNotificationRead(n.id);
      } catch {
        // non-fatal
      }
    }
    if (n.data?.action_url) navigate(n.data.action_url);
  };

  const handleDelete = async (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    const previous = items;
    setItems((rows) => rows.filter((r) => r.id !== id));
    try {
      await apiService.deleteNotification(id);
    } catch {
      setItems(previous); // rollback on failure
    }
  };

  const handleMarkAllRead = async () => {
    setItems((rows) => rows.map((r) => ({ ...r, is_read: true })));
    try {
      await apiService.markAllNotificationsRead();
    } catch {
      // non-fatal
    }
  };

  const filterBtn = (value: 'all' | 'unread', label: string) => (
    <button
      onClick={() => setFilter(value)}
      className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wider border transition-colors ${
        filter === value
          ? 'bg-ink-900 text-paper border-ink-900'
          : 'bg-white text-secondary-600 border-secondary-300 hover:border-ink-900'
      }`}
    >
      {label}
    </button>
  );

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 border-b-2 border-ink-900 pb-4 mb-6">
        <h1 className="font-display text-3xl uppercase tracking-tight text-ink-900">Notifications</h1>
        <div className="flex items-center gap-2">
          {filterBtn('all', 'All')}
          {filterBtn('unread', 'Unread')}
          <button
            onClick={handleMarkAllRead}
            className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-secondary-500 hover:text-ink-900"
          >
            Mark all read
          </button>
        </div>
      </div>

      {error && <p className="text-sm text-danger mb-4">{error}</p>}

      {!loading && items.length === 0 && !error ? (
        <p className="py-16 text-center font-display text-lg uppercase tracking-wider text-secondary-400">
          Nothing on record.
        </p>
      ) : (
        <div className="bg-white border border-secondary-200 divide-y divide-secondary-200">
          {items.map((n) => (
            <div
              key={n.id}
              role="button"
              tabIndex={0}
              onClick={() => handleRowClick(n)}
              onKeyDown={(e) => {
                if (e.target !== e.currentTarget) return; // nested controls (delete) handle their own keys
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleRowClick(n);
                }
              }}
              className={`flex items-start gap-3 px-4 py-3.5 cursor-pointer hover:bg-secondary-50 transition-colors ${
                n.is_read ? '' : 'border-l-2 border-gold bg-gold/5'
              }`}
            >
              <div className="flex-1 min-w-0">
                <p className={`text-sm ${n.is_read ? 'text-secondary-700' : 'font-semibold text-ink-900'}`}>
                  {n.title}
                </p>
                {n.message && <p className="text-sm text-secondary-500 mt-0.5">{n.message}</p>}
                <p className="text-xs text-secondary-400 mt-1">{timeAgo(n.created_at)}</p>
              </div>
              <button
                onClick={(e) => handleDelete(e, n.id)}
                aria-label="Delete notification"
                className="icon-btn p-2 text-secondary-300 hover:text-danger flex-shrink-0"
              >
                <TrashIcon className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {hasMore && (
        <div className="mt-4 text-center">
          <button
            onClick={() => load(filter === 'unread', items.length)}
            disabled={loading}
            className="btn px-4 py-2 text-xs font-semibold uppercase tracking-wider border border-ink-900 text-ink-900 hover:bg-ink-900 hover:text-paper disabled:opacity-50"
          >
            {loading ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </div>
  );
}
